/**
 * useLatest — mirror a React state value into a ref that is ALWAYS up to
 * date, so a callback / event handler that reads the value doesn't get
 * bitten by React's async state commit lifecycle.
 *
 * Rationale: several places in the chat pipeline (conversation_id, voice
 * choice, focus_mode) are read inside a `fetch` body that may execute
 * between two React renders — if we read from state directly, the fetch
 * captures a STALE snapshot and the backend gets the wrong value. Wrapping
 * such values in `useLatest` means every read is authoritative.
 *
 * Usage:
 *   const [voice, setVoice] = useState("hale");
 *   const voiceRef = useLatest(voice);
 *   // later, in a fetch body called from a rapid event:
 *   body: JSON.stringify({ voice: voiceRef.current })
 */
import { useEffect, useRef } from "react";

export function useLatest(value) {
  const ref = useRef(value);
  useEffect(() => { ref.current = value; }, [value]);
  return ref;
}
